<template>
  <AppDialogContent
    v-model="showForm"
    :error="formError"
    :persistent="formSubmited"
    :loading="formSubmited"
    data-test="horseMeasurementForm"
    @submit-form="handleSubmit()"
  >
    <template #header>
      {{
        $t(
          "horses:horseProfilePage.PonyMeasurementSection.heading",
          "Add pony measurement",
          "heading pony measurement section"
        )
      }}
    </template>

    <AppLoadingPage v-if="$apollo.queries.initPonyMeasurementForm.loading" />

    <template v-else>
      <AppDatePicker
        v-if="v$.form.measurementDate"
        v-model="form.measurementDate"
        :min="measurementDateMin"
        :max="measurementDateMax"
        :error-messages="fieldErrorMessages('measurementDate')"
        data-test="horseMeasurementDate"
        :label="
          $t(
            'horses:horseProfilePage.PonyMeasurementSection.measurementDate.label',
            'Measurement date',
            'label pony measurement section'
          )
        "
        @blur="v$.form.measurementDate.$touch()"
      />

      <v-text-field
        v-if="v$.form.height"
        v-model.number="form.height"
        :error-messages="fieldErrorMessages('height')"
        mask="###"
        :label="
          $t(
            'horses:horseProfilePage.PonyMeasurementSection.height.label',
            'Height',
            'label pony measurement section'
          )
        "
        :hint="
          $t(
            'horses:horseProfilePage.PonyMeasurementSection.height.hint',
            'Only in ### number format',
            'hint pony measurement section'
          )
        "
        data-test="horseHeight"
        persistent-hint
        variant="outlined"
        scrollable
        @blur="v$.form.height.$touch()"
      />

      <div v-if="v$.form.withHorseshoe" data-test="horseShoes" class="mb-5">
        <v-radio-group
          v-model="form.withHorseshoe"
          class="mt-0"
          :disabled="fieldDisabled('withHorseshoe')"
          column
        >
          <template #label>
            <span style="font-size: 16px; font-weight: bold">{{
              $t(
                "horses:horseProfilePage.PonyMeasurementSection.ponyMesuredWithOrWithoutsShoes.label",
                "Was pony measured with or without shoes?"
              )
            }}</span>
          </template>
          <v-radio
            :label="
              $t(
                'horses:horseProfilePage.PonyMeasurementSection.withShoes.label',
                'with shoes'
              )
            "
            data-test="withShoes"
            :value="true"
          />
          <v-radio
            :label="
              $t(
                'horses:horseProfilePage.PonyMeasurementSection.withoutShoes.label',
                'without shoes'
              )
            "
            data-test="noShoes"
            :value="false"
          />
        </v-radio-group>
      </div>

      <v-autocomplete
        v-if="v$.form.measurerPersonId"
        v-model="form.measurerPersonId"
        :items="veterinarians"
        :error-messages="fieldErrorMessages('measurerPersonId')"
        :disabled="fieldDisabled('measurerPersonId')"
        :label="
          $t(
            'horses:horseProfilePage.PonyMeasurementSection.measurerPersonId.label',
            'Measuring veterinarian from list of approved veterinarians'
          )
        "
        :hint="
          $t(
            'horses:horseProfilePage.PonyMeasurementSection.measurerPersonId.hint',
            'if not found, add as a text below. No need to add to both fields, if veterinarian can be found from the list.'
          )
        "
        persitent-hint
        clearable
        data-test="measurerPersonId"
        variant="outlined"
        @blur="v$.form.measurerPersonId.$touch()"
      />

      <v-text-field
        v-if="v$.form.veterinarianName"
        v-model="form.veterinarianName"
        :error-messages="fieldErrorMessages('veterinarianName')"
        :disabled="fieldDisabled('veterinarianName')"
        :label="
          $t(
            'horses:horseProfilePage.PonyMeasurementSection.veterinarianName.label',
            'Measuring veterinarian'
          )
        "
        :hint="
          $t(
            'horses:horseProfilePage.PonyMeasurementSection.veterinarianName.hint',
            'Add name of veterinarian that measured a horse.'
          )
        "
        data-test="veterinarianName"
        variant="outlined"
        @blur="v$.form.veterinarianName.$touch()"
      />

      <v-textarea
        v-if="v$.form.comment"
        v-model="form.comment"
        :label="
          $t(
            'horses:horseProfilePage.PonyMeasurementSection.comment.label',
            'Comment',
            'label pony measurement section'
          )
        "
        variant="outlined"
        data-test="horseMeasurementComment"
      />
    </template>
  </AppDialogContent>
</template>
<script>
import { defineComponent, ref } from "vue";
import gql from "fraql";
import { useFormValidation } from "@/composables/form/form_validation";
import { useSaveMeta } from "@/composables/form/save_meta";
import AppDialogContent from "@/components/elements/AppDialogContent.vue";
import AppDatePicker from "@/components/elements/AppDatePicker.vue";
import AppLoadingPage from "@elements/AppLoadingPage.vue";

export default defineComponent({
  name: "HorsePonyMeasurementForm",
  components: {
    AppDialogContent,
    AppDatePicker,
    AppLoadingPage
  },
  emits: ["update"],
  setup() {
    const form = ref({});
    const initPonyMeasurementForm = ref({
      vuelidateValidations: {},
      readOnlyFields: [],
      formFields: []
    });
    const { v$, fieldDisabled, fieldErrorMessages, legacyFormErrorMessages } =
      useFormValidation(initPonyMeasurementForm, form, {
        legacy: {
          appFormMixinI18MainKey:
            "horses:horseProfilePage.PonyMeasurementSection"
        }
      });

    const { saveMetaSetOriginalForm, saveMetaConfirmUnsaveChanges } =
      useSaveMeta(form, { usageInModal: true });

    return {
      form,
      saveMetaSetOriginalForm,
      saveMetaConfirmUnsaveChanges,
      initPonyMeasurementForm,

      v$,
      legacyFormErrorMessages,
      fieldDisabled,
      fieldErrorMessages
    };
  },
  data() {
    return {
      showForm: false,
      formSubmited: false,
      formError: {}
    };
  },
  apollo: {
    initPonyMeasurementForm: {
      query: gql`
        query initPonyMeasurementForm($id: ID, $horseId: ID) {
          initPonyMeasurementForm(id: $id, horseId: $horseId) {
            formFields {
              name
              possibleValues
            }
            vuelidateValidations
            readOnlyFields
          }
        }
      `,
      skip() {
        return this.showForm == false;
      },
      variables() {
        return {
          id: this.form.id,
          horseId: this.form.horseId
        };
      },
      manual: true,
      result({ data, loading }) {
        if (loading) return;
        this.initPonyMeasurementForm = data.initPonyMeasurementForm;
      }
    }
  },
  computed: {
    measurementDateMin() {
      return this.v$.form?.measurementDate?.minValue?.$params?.minISO;
    },
    measurementDateMax() {
      return this.v$.form?.measurementDate?.maxValue?.$params?.maxISO;
    },
    veterinarians() {
      return (
        this.initPonyMeasurementForm.formFields.find(
          x => x.name == "measurerPersonId"
        )?.possibleValues ?? []
      );
    }
  },
  watch: {
    async showForm(newVal) {
      if (newVal === false) {
        if ((await this.saveMetaConfirmUnsaveChanges()) === true) {
          this.showForm = false;
          this.form = {
            horseId: this.horseId,
            measurementDate: null
          };
          this.saveMetaSetOriginalForm(this.form);
        } else {
          this.showForm = true;
        }
      }
    },
    "form.measurementDate"(newDate) {
      if (newDate === null) return;
      var maxDate = this.$dayjs().add(1, "days");
      if (this.$dayjs(newDate).isBefore(maxDate)) return;
      else this.form.measurementDate = null;
    }
  },
  methods: {
    openForm(measurement) {
      if (!measurement) measurement = {};
      this.form = {
        id: measurement.id ?? null,
        horseId: measurement.horseId,
        measurementDate: measurement.measurementDate ?? null,
        height: measurement.height ?? null,
        comment: measurement.comment ?? null,
        withHorseshoe: measurement.withHorseshoe ?? true,
        measurerPersonId: measurement.measurerPersonId ?? null,
        veterinarianName: measurement.veterinarianName ?? null
      };
      this.saveMetaSetOriginalForm(this.form);
      this.formError = {};
      this.v$.form.$reset();
      this.showForm = true;
    },
    handleSubmit() {
      this.v$.form.$touch();
      if (this.v$.form.$invalid) {
        this.formError = this.legacyFormErrorMessages();
        return false;
      }
      this.formError = {};
      this.v$.form.$reset();
      this.formSubmited = true;

      if (this.form.id) this.updateMeasurement();
      else this.addMeasurement();
    },
    async addMeasurement() {
      try {
        await this.$apollo.mutate({
          mutation: gql`
            mutation horseAddPonyMeasurement(
              $horseId: ID!
              $measurementDate: Date!
              $height: Float!
              $measurerPersonId: ID
              $veterinarianName: String
              $withHorseshoe: Boolean
              $comment: String
            ) {
              horseAddPonyMeasurement(
                input: {
                  horseId: $horseId
                  measurementDate: $measurementDate
                  height: $height
                  measurerPersonId: $measurerPersonId
                  veterinarianName: $veterinarianName
                  withHorseshoe: $withHorseshoe
                  comment: $comment
                }
              ) {
                ponyMeasurement {
                  id
                  measurementDate
                  height
                  comment
                  horseId
                  effectiveFromDate
                  effectiveUntilDate
                  withHorseshoe
                  measurerPersonId
                  veterinarianName
                  isDeleted
                  resultingType
                  allowedMutations
                  horse {
                    id
                    horseType
                    ponyMeasurementEffectiveUntilDate
                  }
                }
              }
            }
          `,
          variables: this.form
        });
        // Result
        this.saveMetaSetOriginalForm(this.form);
        this.$root.$snackbar("success");
        this.$emit("update", true);
        this.formSubmited = false;
        this.showForm = false;
      } catch (error) {
        // Error
        this.formError = { type: "graphql", err: error };
        this.formSubmited = false;
        throw error;
      }
    },
    async updateMeasurement() {
      try {
        await this.$apollo.mutate({
          mutation: gql`
            mutation horseUpdatePonyMeasurement(
              $id: ID!
              $height: Float
              $measurerPersonId: ID
              $veterinarianName: String
              $withHorseshoe: Boolean
              $comment: String
            ) {
              horseUpdatePonyMeasurement(
                input: {
                  id: $id
                  height: $height
                  measurerPersonId: $measurerPersonId
                  veterinarianName: $veterinarianName
                  withHorseshoe: $withHorseshoe
                  comment: $comment
                }
              ) {
                ponyMeasurement {
                  id
                  measurementDate
                  height
                  comment
                  horseId
                  effectiveFromDate
                  effectiveUntilDate
                  withHorseshoe
                  measurerPersonId
                  veterinarianName
                  isDeleted
                  resultingType
                  allowedMutations
                  horse {
                    id
                    horseType
                    ponyMeasurementEffectiveUntilDate
                  }
                }
              }
            }
          `,
          variables: this.form
        });
        // Result
        this.saveMetaSetOriginalForm(this.form);
        this.$root.$snackbar("success");
        //this.$emit("update", true);
        this.formSubmited = false;
        this.showForm = false;
      } catch (error) {
        // Error
        this.formError = { type: "graphql", err: error };
        this.formSubmited = false;
        throw error;
      }
    }
  },
  fragments: {
    ponyMeasurementFields: gql`
      fragment _ on PonyMeasurement {
        id
        horseId
        measurementDate
        height
        comment
        withHorseshoe
        measurerPersonId
        veterinarianName
      }
    `
  }
});
</script>
